import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config';
import { IoVideocam } from 'react-icons/io5';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          prompt: (callback?: (notification: { isNotDisplayed?: boolean; isSkippedMoment?: boolean; isDismissedMoment?: boolean }) => void) => void;
          renderButton: (element: HTMLElement, config: {
            type?: string;
            theme?: string;
            size?: string;
            text?: string;
            shape?: string;
            logo_alignment?: string;
            width?: string;
            locale?: string;
          }) => void;
        };
      };
    };
  }
}

interface LoginScreenProps {
  onLogin?: () => void;
  inviteToken?: string | null;
  inviteSenderName?: string | null;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin, inviteToken, inviteSenderName }) => {
  const { login, loading, error, setError } = useAuth();
  const navigate = useNavigate();
  const [gsiReady, setGsiReady] = useState(false);
  const [inviteCode, setInviteCode] = useState(inviteToken ?? '');
  const buttonRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef<(c: string) => void>(() => {});
  onCredentialRef.current = async (credential: string) => {
    try {
      setError(null);
      await login(credential, inviteCode.trim() || null);
      onLogin?.();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Login failed.';
      setError(msg);
    }
  };

  useEffect(() => {
    setInviteCode(inviteToken ?? '');
  }, [inviteToken]);

  useEffect(() => {
    const cid = API_CONFIG.googleClientId;
    if (!cid) {
      setGsiReady(false);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://accounts.google.com/gsi/client`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (typeof window.google !== 'undefined' && window.google.accounts?.id?.initialize) {
        window.google.accounts.id.initialize({
          client_id: cid,
          callback: (response) => {
            if (response.credential) {
              onCredentialRef.current(response.credential);
            } else {
              setError('No credential from Google.');
            }
          },
        });
        setGsiReady(true);
        
        // Render Google button after a short delay to ensure DOM is ready
        setTimeout(() => {
          if (buttonRef.current && window.google?.accounts?.id?.renderButton) {
            try {
              window.google.accounts.id.renderButton(buttonRef.current, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                width: '320',
              });
            } catch (err) {
              console.error('Failed to render Google button:', err);
              setError('Failed to initialize Google Sign-In button.');
            }
          }
        }, 100);
      }
    };
    document.head.appendChild(script);
    return () => {
      script.remove();
      setGsiReady(false);
    };
  }, [setError]);

  return (
    <div className="min-h-screen bg-[#09090B] bg-[radial-gradient(circle_at_20%_20%,rgba(124,58,237,0.26),transparent_34%),radial-gradient(circle_at_80%_70%,rgba(59,130,246,0.20),transparent_32%)] flex items-center justify-center p-4 text-white">
      <div className="max-w-md w-full bg-[#09090B]/75 backdrop-blur-xl rounded-3xl shadow-2xl shadow-[#7C3AED]/15 border border-[#7C3AED]/25 p-8">
        <div className="text-center mb-8">
          <h1 className="font-brand text-3xl font-extrabold text-white tracking-tight">whizpr</h1>
          <nav className="mt-3 flex items-center justify-center gap-2 text-sm font-extrabold text-white">
            <Link
              to="/privacy-policy"
              className="rounded-full border border-[#7C3AED]/50 bg-[#7C3AED]/20 px-3 py-1.5 shadow-lg shadow-[#7C3AED]/10 transition hover:border-[#A78BFA] hover:bg-[#7C3AED]/35"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="rounded-full border border-[#3B82F6]/50 bg-[#3B82F6]/20 px-3 py-1.5 shadow-lg shadow-[#3B82F6]/10 transition hover:border-[#93C5FD] hover:bg-[#3B82F6]/35"
            >
              Terms of Service
            </Link>
          </nav>
          <p className="text-[#A1A1AA] mt-3">
            {inviteSenderName
              ? `${inviteSenderName} invited you. Sign in to continue.`
              : 'Sign in to start chatting'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-4">
          <label className="block text-left">
            <span className="mb-2 block text-sm font-medium text-[#D4D4D8]">Invite code</span>
            <input
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Paste invite code"
              className="w-full rounded-xl border border-[#7C3AED]/30 bg-white/5 px-4 py-3 text-white placeholder:text-[#71717A] outline-none transition focus:border-[#A78BFA] focus:bg-white/10"
              autoComplete="off"
              spellCheck={false}
            />
          </label>

          {!API_CONFIG.googleClientId ? (
            <p className="text-center text-[#A1A1AA] text-sm">
              Set REACT_APP_GOOGLE_CLIENT_ID in .env to enable Google sign-in.
            </p>
          ) : (
            <div className="w-full flex justify-center">
              {gsiReady ? (
                <div
                  ref={buttonRef}
                  id="google-signin-button"
                  className="w-full flex justify-center"
                  style={{ minHeight: '40px' }}
                />
              ) : (
                <button
                  type="button"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 border border-[#7C3AED]/30 rounded-xl text-white font-medium flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {loading ? 'Signing in…' : 'Loading Google Sign-In…'}
                </button>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-[#09090B] text-[#A1A1AA]">OR</span>
            </div>
          </div>

          {/* Call Without Login Button */}
          <button
            onClick={() => navigate('/call-without-login')}
            className="w-full py-3 px-4 bg-gradient-to-r from-[#7C3AED] via-[#3B82F6] to-[#06B6D4] rounded-xl text-white font-semibold flex items-center justify-center gap-3 transition-all transform hover:scale-[1.02] shadow-lg shadow-[#3B82F6]/20"
          >
            <IoVideocam size={20} />
            Call Without Login
          </button>
          
          <p className="text-center text-[#A1A1AA] text-xs mt-2">
            Make instant calls without signing in
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
